import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkle, BellRinging, MagnifyingGlass } from "@phosphor-icons/react";

export default function OrgProfileAssistant({ onKeywordSelect }) {
  const navigate = useNavigate();
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [description, setDescription] = useState("");
  const [profile, setProfile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [savedKeywords, setSavedKeywords] = useState({});

  useEffect(() => {
    api.get("/org-profile").then(({ data }) => {
      if (data.profile) {
        setProfile(data.profile);
        setWebsiteUrl(data.profile.website_url || "");
        setDescription(data.profile.description || "");
      }
    }).catch(() => {});
  }, []);

  const analyze = async () => {
    if (!websiteUrl.trim() && !description.trim()) {
      toast.error("Enter your website URL or a few keywords about your organization.");
      return;
    }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/org-profile/analyze", {
        website_url: websiteUrl.trim() || null,
        description: description.trim() || null,
      });
      setProfile(data.profile);
      if (data.website_fetch_error) toast.warning(`${data.website_fetch_error} — analyzed your description instead.`);
      else toast.success("Analysis complete — click a keyword to search.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const saveAlert = async (kw) => {
    try {
      await api.post("/saved-searches", { name: kw, keyword: kw, frequency: "daily" });
      setSavedKeywords((s) => ({ ...s, [kw]: true }));
      toast.success(`Alert saved — we'll email you new "${kw}" grants.`);
      return true;
    } catch (e) {
      if (e.response?.status === 402) {
        toast("Email alerts are a premium feature.", {
          description: "Upgrade to get daily emails when new matching grants post.",
          action: { label: "Upgrade", onClick: () => navigate("/subscribe") },
        });
        return false;
      }
      toast.error(e.response?.data?.detail || "Failed to save alert");
      return false;
    }
  };

  const saveAllAlerts = async () => {
    for (const kw of profile.keywords) {
      if (!savedKeywords[kw]) {
        const ok = await saveAlert(kw); // sequential keeps toasts readable
        if (!ok) break;
      }
    }
  };

  return (
    <div className="border-2 border-zinc-900 bg-white p-5" data-testid="org-assistant">
      <div className="flex items-center gap-2">
        <Sparkle size={16} weight="fill" className="text-amber-500" />
        <span className="gf-overline">AI search assist</span>
      </div>
      <p className="text-sm text-zinc-600 mt-1">
        Paste your organization&apos;s website or a few keywords — we&apos;ll suggest the grant-search
        terms funders actually use.
      </p>
      <div className="mt-3 grid sm:grid-cols-[1fr_1fr_auto] gap-2">
        <Input
          data-testid="org-website-input"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="yourorganization.org"
          className="gf-sharp border-zinc-300"
        />
        <Input
          data-testid="org-description-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="…or keywords (e.g. youth literacy nonprofit)"
          className="gf-sharp border-zinc-300"
        />
        <Button
          onClick={analyze}
          disabled={analyzing}
          data-testid="org-analyze-btn"
          className="gf-sharp bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wide"
        >
          {analyzing ? "Analyzing…" : "Analyze"}
        </Button>
      </div>

      {profile && (
        <div className="mt-4" data-testid="org-profile-result">
          {profile.mission && (
            <p className="text-sm text-zinc-700"><b>What we understood:</b> {profile.mission}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {profile.keywords.map((kw) => (
              <span key={kw} className="inline-flex items-stretch border border-zinc-300 bg-zinc-50 hover:border-blue-700 transition-colors">
                <button
                  type="button"
                  onClick={() => onKeywordSelect && onKeywordSelect(kw)}
                  data-testid={`org-keyword-${kw.replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-zinc-900 hover:text-blue-700"
                  title={`Search grants for "${kw}"`}
                >
                  <MagnifyingGlass size={11} /> {kw}
                </button>
                <button
                  type="button"
                  onClick={() => saveAlert(kw)}
                  disabled={savedKeywords[kw]}
                  data-testid={`org-keyword-alert-${kw.replace(/\s+/g, "-")}`}
                  className={`px-2 border-l border-zinc-200 ${savedKeywords[kw] ? "text-emerald-600" : "text-zinc-400 hover:text-blue-700"}`}
                  title={savedKeywords[kw] ? "Alert saved" : `Save "${kw}" as a daily alert`}
                >
                  <BellRinging size={13} weight={savedKeywords[kw] ? "fill" : "regular"} />
                </button>
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={saveAllAlerts}
            data-testid="org-save-all-alerts"
            className="mt-3 text-[11px] font-mono uppercase tracking-widest text-blue-700 hover:text-blue-900"
          >
            <BellRinging size={11} className="inline mr-1" />
            Save all as daily email alerts
          </button>
        </div>
      )}
    </div>
  );
}
